import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Button, Typography, Chip, IconButton, Grid, TablePagination, Tooltip,
  MenuItem, Select, InputLabel, FormControl
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  CheckCircle as PostIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from '../../../axios';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const JournalEntriesList = () => {
  const { t, i18n } = useTranslation();
  // Removed useSearchParams usage for filtering by default
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchEntries();
  }, [page, rowsPerPage, searchTerm, fromDate, toDate, filterType]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      // Removed entryType param to fetch all entries
      const response = await axios.get('/JournalEntries', {
        params: { 
            page: page + 1, 
            pageSize: rowsPerPage, 
            search: searchTerm,
            entryType: filterType || null,
            fromDate: fromDate || null,
            toDate: toDate || null
        }
      });
      setEntries(response.data.items);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`/JournalEntries/${id}`);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert(error.response?.data || 'Error deleting');
    }
  };

  const handlePost = async (id) => {
    if (!window.confirm(t('confirmPost'))) return;
    try {
      await axios.post(`/JournalEntries/${id}/post`);
      fetchEntries();
    } catch (error) {
      console.error('Error posting entry:', error);
      alert(error.response?.data || 'Error posting');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd/MM/yyyy', {
      locale: i18n.language === 'ar' ? ar : undefined
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return t('draft');
      case 2: return t('posted');
      case 3: return t('canceled');
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'default';
      case 2: return 'success';
      case 3: return 'error';
      default: return 'default';
    }
  };
  
  const getTypeLabel = (type) => {
      const types = {
          1: t('dailyEntry'),
          2: "القيود الافتتاحية", // Hardcoded for now if translation key missing
          3: t('salesEntry'),
          4: t('salesReturnEntry'),
          5: t('purchaseEntry'),
          6: t('purchaseReturnEntry'),
          7: t('paymentVoucher'),
          8: t('receiptVoucher'),
          9: t('transferEntry'),
          10: t('adjustmentEntry'),
          11: t('depreciationEntry'),
          12: t('payrollEntry'),
          13: t('closingEntry')
      };
      return types[type] || type;
  };

  const getPageTitle = () => {
      return t('journalEntries');
  };
  
  const entryTypes = [
    { value: 1, label: t('dailyEntry') },
    { value: 2, label: "القيود الافتتاحية" }, // localized properly if key exists
    { value: 3, label: t('salesEntry') },
    { value: 4, label: t('salesReturnEntry') },
    { value: 5, label: t('purchaseEntry') },
    { value: 6, label: t('purchaseReturnEntry') },
    { value: 7, label: t('paymentVoucher') },
    { value: 8, label: t('receiptVoucher') },
    { value: 9, label: t('transferEntry') },
    { value: 10, label: t('adjustmentEntry') },
    { value: 11, label: t('depreciationEntry') },
    { value: 12, label: t('payrollEntry') },
    { value: 13, label: t('closingEntry') }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            {getPageTitle()}
          </Typography>
          <Box>
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => window.location.href = '/journal-entries/new?type=2'}
                sx={{ mr: 2 }}
            >
                {t('قيد افتتاحي') || "addOpeningEntry"}
            </Button>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => window.location.href = '/journal-entries/new?type=1'}
            >
                {t('قيد يومية')}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label={t('search')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                  <InputLabel>{t('entryType')}</InputLabel>
                  <Select
                      value={filterType}
                      label={t('entryType')}
                      onChange={(e) => setFilterType(e.target.value)}
                  >
                      <MenuItem value="">{t('all')}</MenuItem>
                      {entryTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                      ))}
                  </Select>
              </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
              <TextField
                  fullWidth
                  label={t('fromDate')}
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
              />
          </Grid>
          <Grid item xs={12} sm={2}>
              <TextField
                  fullWidth
                  label={t('toDate')}
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
              />
          </Grid>
          <Grid item xs={12} sm={3} sx={{ display: 'flex', gap: 1 }}>
               <Button 
                  variant="outlined" 
                  onClick={() => {
                      setSearchTerm('');
                      setFilterType('');
                      setFromDate('');
                      setToDate('');
                  }}
                  size="small"
               >
                   {t('مسح الفلاتر')}
               </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('رقم القيد')}</TableCell>
              <TableCell>{t('التاريخ')}</TableCell>
              <TableCell>{t('النوع')}</TableCell>
              <TableCell>{t('الوصف')}</TableCell>
              <TableCell>{t('إجمالي مدين')}</TableCell>
              <TableCell>{t('إجمالي دائن')}</TableCell>
              <TableCell>{t('الحالة')}</TableCell>
              <TableCell align="center">{t('إجراءات')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">{t('loading')}</TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">{t('noEntriesFound')}</TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.globalId} hover>
                  <TableCell>{entry.entryNumber}</TableCell>
                  <TableCell>{formatDate(entry.date)}</TableCell>
                  <TableCell>{getTypeLabel(entry.entryType)}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell>{entry.totalDebit.toLocaleString()}</TableCell>
                  <TableCell>{entry.totalCredit.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={getStatusLabel(entry.status)} color={getStatusColor(entry.status)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('view')}>
                        <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => window.location.href = `/journal-entries/${entry.globalId}/view`}
                      >
                        <ViewIcon />
                      </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('edit')}>
                        <span>
                      <IconButton
                        size="small"
                        disabled={entry.status !== 1}
                        onClick={() => window.location.href = `/journal-entries/${entry.globalId}/edit`}
                      >
                        <EditIcon />
                      </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('post')}>
                        <span>
                      <IconButton
                        size="small"
                        color="success"
                        disabled={entry.status !== 1}
                        onClick={() => handlePost(entry.globalId)}
                      >
                        <PostIcon />
                      </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('delete')}>
                        <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={entry.status !== 1}
                        onClick={() => handleDelete(entry.globalId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default JournalEntriesList;
